// Boss Fights (ADR-0025) detail page. Phase 1: draft editing +
// metadata + archive. Latch (Start) / damage / close land in
// Phases 2-3.

import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { ingestorsApi } from '../ingestors/api';
import type { Badge } from '../ingestors/types';
import { bossesApi } from './api';
import type { BossStatus } from './types';

function StatusBadge({ status }: { status: BossStatus }) {
  const map: Record<BossStatus, string> = {
    draft: 'bg-slate-700/40 text-slate-300 border-slate-600',
    active: 'bg-red-800/40 text-red-200 border-red-700',
    vanquished: 'bg-emerald-700/40 text-emerald-200 border-emerald-600',
    survived: 'bg-amber-700/40 text-amber-200 border-amber-600',
    archived: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
  };
  return <span className={`px-2 py-0.5 text-xs rounded border ${map[status]}`}>{status}</span>;
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function BossDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['admin-boss', id],
    queryFn: () => bossesApi.get(id),
    enabled: !!id,
  });

  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });

  // Draft edit form state.
  // v0.8.9: dropped HP-multiplier and damage-event-type knobs from
  // the edit form — the scoring engine ignores both.
  const [editName, setEditName] = useState('');
  const [editEpic, setEditEpic] = useState('');
  const [editStarts, setEditStarts] = useState('');
  const [editEnds, setEditEnds] = useState('');
  const [editRewardBadge, setEditRewardBadge] = useState('');

  const boss = detail.data?.boss;
  const isDraft = boss?.status === 'draft';
  useEffect(() => {
    if (boss) {
      setEditName(boss.name);
      setEditEpic(boss.jira_epic_key ?? '');
      setEditStarts(toLocalInput(boss.starts_at));
      setEditEnds(toLocalInput(boss.ends_at));
      setEditRewardBadge(boss.reward_badge_id ?? '');
    }
  }, [boss]);

  const update = useMutation({
    mutationFn: () => bossesApi.update(id, {
      name: editName,
      jira_epic_key: editEpic || null,
      starts_at: new Date(editStarts).toISOString(),
      ends_at: new Date(editEnds).toISOString(),
      reward_badge_id: editRewardBadge || null,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-boss', id] }),
  });

  const archive = useMutation({
    mutationFn: () => bossesApi.archive(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-boss', id] }),
  });

  // Phase 2: latch (draft → active) + live damage progress.
  const start = useMutation({
    mutationFn: () => bossesApi.start(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-boss', id] }),
  });
  const isActive = boss?.status === 'active';
  const progress = useQuery({
    queryKey: ['boss-progress', id],
    queryFn: () => bossesApi.progress(id),
    enabled: !!id && isActive,
    refetchInterval: isActive ? 15000 : false,
  });
  // Phase 3: End + reward badge.
  const end = useMutation({
    mutationFn: () => bossesApi.end(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-boss', id] }),
  });
  const badges = useQuery({
    queryKey: ['badges'],
    queryFn: () => ingestorsApi.listBadges(),
  });

  if (detail.isPending) return <div className="p-6 text-ink-muted">Loading…</div>;
  if (detail.isError || !boss) return (
    <div className="p-6 text-red-400">
      Failed to load boss. <Link to="/admin/bosses" className="text-brand hover:underline">← Back to list</Link>
    </div>
  );

  const teamName = teams.data?.teams.find((t) => t.id === boss.team_id)?.name ?? boss.team_id;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-ink">{boss.name}</h1>
            <StatusBadge status={boss.status} />
          </div>
          <p className="text-sm text-ink-muted mt-1">{teamName} · {boss.jira_epic_key ? `epic ${boss.jira_epic_key}` : 'no epic'} · created {new Date(boss.created_at).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-3">
          {boss.status === 'draft' && boss.jira_epic_key && (
            <button
              onClick={() => {
                if (!window.confirm('Latch this boss? Its HP pool is snapshotted from the epic\'s children now.')) return;
                start.mutate();
              }}
              disabled={start.isPending}
              className="px-3 py-1.5 bg-brand text-white text-sm rounded hover:bg-brand-dark transition disabled:opacity-50"
            >
              {start.isPending ? 'Latching…' : 'Latch boss'}
            </button>
          )}
          {boss.status === 'active' && (
            <button
              onClick={() => {
                if (!window.confirm('End this boss fight? Final damage is computed and the cosmetic badge awarded on a win.')) return;
                end.mutate();
              }}
              disabled={end.isPending}
              className="px-3 py-1.5 bg-red-700 text-white text-sm rounded hover:bg-red-800 transition disabled:opacity-50"
            >
              {end.isPending ? 'Ending…' : 'End fight'}
            </button>
          )}
          <Link to="/admin/bosses" className="text-sm text-ink-muted hover:text-ink">← Back to list</Link>
        </div>
      </div>

      {isActive && progress.data && (
        <div className="mb-4 bg-bg-elev border border-border rounded p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-ink">Boss HP</h3>
            {progress.data.status === 'vanquished' && <span className="text-xs text-emerald-300">vanquished!</span>}
          </div>
          {boss.hp !== null && boss.hp > 0 ? (
            <>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-semibold text-red-300">{Math.round(progress.data.damage_dealt)}</span>
                <span className="text-sm text-ink-muted">/ {boss.hp} damage</span>
              </div>
              <div className="h-2 bg-bg-base rounded overflow-hidden">
                <div className="h-full bg-red-500" style={{ width: `${Math.min(100, (progress.data.damage_dealt / boss.hp) * 100)}%` }} />
              </div>
            </>
          ) : (
            <div className="text-sm text-ink-muted">HP not set (latch an epic to give this boss HP). Any damage vanquishes it.</div>
          )}
          {progress.data.per_user_damage.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-ink-muted">
              {progress.data.per_user_damage.map((u) => (
                <li key={u.user_id} className="flex justify-between"><span>{u.display_name ?? u.user_id}</span><span>{u.damage} damage</span></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isDraft ? (
        <form onSubmit={(e) => { e.preventDefault(); update.mutate(); }} className="bg-bg-elev border border-border rounded p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Name</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Jira epic key</label>
              <input value={editEpic} onChange={(e) => setEditEpic(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Starts</label>
              <input type="datetime-local" value={editStarts} onChange={(e) => setEditStarts(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Ends</label>
              <input type="datetime-local" value={editEnds} onChange={(e) => setEditEnds(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Reward badge (awarded to the whole team on a win)</label>
            <select value={editRewardBadge} onChange={(e) => setEditRewardBadge(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm">
              <option value="">None</option>
              {(badges.data?.badges ?? []).map((b: Badge) => <option key={b.id} value={b.id}>{b.icon} {b.name}</option>)}
            </select>
          </div>
          {update.isError && <div className="text-sm text-red-400">Save failed.</div>}
          <div className="flex justify-between">
            <button type="button" onClick={() => { if (window.confirm(`Archive boss "${boss.name}"?`)) archive.mutate(); }} className="px-3 py-1.5 text-red-300 hover:underline text-sm disabled:opacity-50" disabled={archive.isPending}>
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </button>
            <button type="submit" disabled={update.isPending} className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">
              {update.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-bg-elev border border-border rounded p-5 text-sm text-ink-muted space-y-2">
          {boss.status === 'active' && <p>This boss is <strong className="text-ink">active</strong>. Latch already taken; damage accrues until the window ends.</p>}
          {boss.status === 'vanquished' && <p>This boss was <strong className="text-emerald-300">vanquished</strong>. Read-only.</p>}
          {boss.status === 'survived' && <p>This boss <strong className="text-amber-300">survived</strong> the window. Read-only.</p>}
          {boss.status === 'archived' && <p>This boss is <strong className="text-ink">archived</strong>. Read-only.</p>}
          <div>Window: {new Date(boss.starts_at).toLocaleString()} → {new Date(boss.ends_at).toLocaleString()}</div>
          {boss.reward_badge_id && (
            <div>Reward: {(badges.data?.badges ?? []).find((b: Badge) => b.id === boss.reward_badge_id)?.name ?? boss.reward_badge_id}</div>
          )}
          {boss.hp_pool.length > 0 && <div>HP pool: {boss.hp_pool.length} tickets</div>}
        </div>
      )}
    </div>
  );
}
